import React, { useEffect, useState, useRef, useContext } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { apiCall } from "../../common/common.js";
import * as pageRoutes from "../../../pageRoutes.js";
import Header1 from "../../common/Header1/Header1";
import SideMenuCommonFilter from "../../../contextApi/SideMenuCommonFilter";
import SideMenuCommonFilterLib from "../../../contextApi/SideMenuCommonFilterLib";
import { FiBook, FiCode, FiSettings, FiChevronsRight, FiFileText, FiChevronDown, FiArrowUp } from "react-icons/fi";
import "./RsUserGuide.scss";
import { boolImgRetailer, generateFiltersArr, generatePaginationString, getCustomLabel, getDownloadThresold, imageSource, isColumnAllowed, isFilterAllowed, noImageCallBack } from "../../common/common";
import { generatePagination, generatePricingFilters } from "../../../utils/routeBasedOperations";
import { useParams } from "react-router-dom";

const getIconForSection = (title) => {
  const lowerTitle = title.toLowerCase();
  if (lowerTitle.includes("introduction") || lowerTitle.includes("getting into")) return <FiBook />;
  if (lowerTitle.includes("setup") || lowerTitle.includes("registration")) return <FiSettings />;
  if (lowerTitle.includes("api")) return <FiCode />;
  if (lowerTitle.includes("usage")) return <FiFileText />;
  return <FiChevronsRight />;
};

export default function RSUserGuidePage() {
  const [activeSubItemId, setActiveSubItemId] = useState(null);
  const [guideSections, setGuideSections] = useState([]);
  const [toc, setToc] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [openSections, setOpenSections] = useState(new Set());
  const [expandedTocItems, setExpandedTocItems] = useState(new Set());
  const [showGoToTop, setShowGoToTop] = useState(false);

  const contentRef = useRef(null);
  const tocRef = useRef(null);

  const [breadcrumbConfig] = useState([{
    href: pageRoutes.homePage,
    title: "Home"
  }, {
    href: null,
    title: "User Guide"
  }]);
  const filterConfig = ["user", "tenant_segment", "category", "subcategory", "subsubcategory", "subsubsubcategory", "brand", "tenant_priority", "retailer", "image_competitor", "postalCode", "tenant_stock_status_flag", "comp_stock_status_flag", "comp_3p_seller_flag", "is_cpi_outlier", "map_violation", "price_relativity", "comp_zipcode"].filter((name) => isFilterAllowed(name));
  const [sideMenuCommonFilterContext, setSideMenuCommonFilterContext] = useContext(SideMenuCommonFilter);
  const [sideMenuCommonFilterLibContext, setSideMenuCommonFilterLibContext] = useContext(SideMenuCommonFilterLib);
  const [filterBtnsSelected, setFilterBtnsSelected] = useState(sideMenuCommonFilterContext);
  const filters = generateFiltersArr(filterConfig, filterBtnsSelected);
  const pages = {
    "pricing-analytics": "rs_user_guide.html",
    "pricing-engine": "rs_user_guide_rules.html",
    "assortment": "rs_user_guide_assortment.html"
  };

  const { page } = useParams();

  const toggleSection = (sectionId) => {
    setOpenSections(prevOpenSections => {
      const newSet = new Set(prevOpenSections);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Fetch and parse user guide HTML
  useEffect(() => {
    async function fetchGuide() {
      let pageString = pages["pricing-analytics"];
      if (page in pages) {
        pageString = pages[page];
      }
      setLoading(true);
      try {
        const res = await apiCall(
          "GET", "/cms", {}, { name: pageString },
          false, false, false
        );
        if (typeof res.data !== "string") {
          toast.warn("User guide not available.");
          setLoading(false);
          return;
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(res.data, "text/html");
        const nodes = Array.from(doc.body.children);
        const newSections = [];
        const newToc = [];
        let h2Count = 0;
        let currentSection = null;

        nodes.forEach(node => {
          const h2 = node.querySelector && node.querySelector('h2');

          if (node.tagName === 'H2' || h2) {
            const headerElement = node.tagName === 'H2' ? node : h2;
            h2Count++;

            if (currentSection) {
              newSections.push(currentSection);
            }

            let text = headerElement.textContent.trim().replace(/^#\s*/, '');
            const slug = text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
            const linkElement = h2 && h2.querySelector ? h2.querySelector('a') : null;

            const sectionWrapperId = `section-wrapper-${slug}`;

            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = node.innerHTML;
            const headerInTemp = tempDiv.querySelector('h2');
            if (headerInTemp) headerInTemp.remove();
            const contentHtml = tempDiv.innerHTML;

            currentSection = {
              id: slug,
              wrapperId: sectionWrapperId,
              title: text,
              link: linkElement ? linkElement.href : null,
              html: contentHtml,
              children: [],
            };

            const tocEntry = {
              id: slug,
              title: `${h2Count}. ${text}`,
              count: 0,
              icon: getIconForSection(text),
              children: [],
            };

            newToc.push(tocEntry);
          } else if (currentSection) {
            if (node.tagName === 'H3' || (node.tagName === 'DIV' && node.querySelector('h3'))) {
              const h3 = node.tagName === 'H3' ? node : node.querySelector('h3');
              const h3Text = h3.textContent.trim().replace(/^\d+\.\s*/, '');
              const h3Slug = h3Text.toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-");
              h3.id = h3Slug;
              const tocChild = { id: h3Slug, title: h3Text };
              const parentToc = newToc[newToc.length - 1];
              if (parentToc) {
                parentToc.children.push(tocChild);
                parentToc.count = parentToc.children.length;
              }
            }
            currentSection.html += node.outerHTML;
          }
        });

        if (currentSection) newSections.push(currentSection);

        setGuideSections(newSections);
        setToc(newToc);

      } catch (err) {
        console.error(err);
        toast.error("Failed to load user guide.");
      } finally {
        setLoading(false);
      }
    }
    fetchGuide();
  }, [page]);

  useEffect(() => {
    if (toc.length > 0 && guideSections.length > 0) {
      const firstTocId = toc[0].id;
      const matchingSection = guideSections.find(sec => sec.id === firstTocId);
      if (matchingSection) {
        setOpenSections(new Set([matchingSection.id]));
        setActiveId(matchingSection.id);
        setExpandedTocItems(new Set([matchingSection.id]));
        setActiveSubItemId(null);
      } else {
        setOpenSections(new Set([guideSections[0].id]));
        setActiveId(guideSections[0].id);
        setExpandedTocItems(new Set([guideSections[0].id]));
        setActiveSubItemId(null);
      }
    }
  }, [guideSections, toc]);

  useEffect(() => {
    if (loading || !toc.length || !contentRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id.replace('section-wrapper-', '');
            setActiveId(sectionId);
          }
        });
      },
      { root: contentRef.current, rootMargin: '0px 0px -80% 0px', threshold: 0 }
    );
    guideSections.forEach((section) => {
      const el = document.getElementById(section.wrapperId);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [loading, guideSections]);

  useEffect(() => {
    const contentEl = contentRef.current;
    if (!contentEl) return;
    const handleScroll = () => setShowGoToTop(contentEl.scrollTop > 400);
    contentEl.addEventListener('scroll', handleScroll);
    return () => contentEl.removeEventListener('scroll', handleScroll);
  }, [loading]);

  const handleTocClick = (sectionId) => {
    setExpandedTocItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
    setActiveSubItemId(null);

    setOpenSections(prev => new Set(prev).add(sectionId));
    setTimeout(() => {
      const targetEl = document.getElementById(`section-wrapper-${sectionId}`);
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handleTocSubItemClick = (sectionId, subItemId) => {
    setActiveSubItemId(subItemId);
    setExpandedTocItems(prev => new Set(prev).add(sectionId));
    setOpenSections(prev => new Set(prev).add(sectionId));
    setTimeout(() => {
      const subItemEl = document.getElementById(subItemId);
      if (subItemEl) {
        subItemEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const scrollToTop = () => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="user-guide-wrapper">
        <div className="user-guide-loading">
          <Spinner animation="border" /> Loading guide…
        </div>
      </div>
    );
  }

  return (
    <>
      <Header1
        breadcrumbConfig={breadcrumbConfig}
        filterBtnsSelected={filterBtnsSelected}
        setFilterBtnsSelected={setFilterBtnsSelected}
        hideSearchBar={true}
        showSavedFilters={false}
        filterConfig={filterConfig}
        refreshConfig={{ showNextRefresh: true, showLastRefresh: true, module: "pricing" }}
        onlyBreadCrumb={true}
        generateFilter={generatePricingFilters}
        hideRefresh={true}
      />
      <div className="user-guide-wrapper">
        <nav className="user-guide-toc" ref={tocRef}>
          <div className="toc-header">Contents</div>
          <ul>
            {toc.map((sec) => (
              <li key={sec.id} className={`toc-section ${activeId === sec.id && !activeSubItemId ? "active" : ""} ${expandedTocItems.has(sec.id) ? "expanded" : ""}`}>
                <button data-id={sec.id} onClick={() => handleTocClick(sec.id)}>
                  <span className="toc-icon">{sec.icon}</span>
                  <span className="toc-section-title">{sec.title}</span>
                  {sec.count > 0 && <span className="toc-badge">{sec.count}</span>}
                </button>
                {sec.children.length > 0 && (
                  <ul className="toc-steps">
                    {sec.children.map((ch) => (
                      <li key={ch.id} className={activeSubItemId === ch.id ? 'active' : ''}>
                        <a href={`#${ch.id}`} onClick={(e) => {
                          e.preventDefault();
                          handleTocSubItemClick(sec.id, ch.id);
                        }}>{ch.title}</a>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>
        <article className="user-guide-content" ref={contentRef}>
          <div className="content-body">
            <h1>User Guide</h1>
            {guideSections.map((section) => {
              const isOpen = openSections.has(section.id);
              const tocEntry = toc.find(t => t.id === section.id);
              const stepCount = tocEntry ? tocEntry.count : 0;

              return (
                <div key={section.id} id={section.wrapperId} className={`guide-section-item ${isOpen ? 'open' : ''}`}>
                  <div className="guide-section-header" onClick={() => toggleSection(section.id)}>
                    <FiChevronDown className="chevron-icon" />
                    <h2>{section.title}</h2>
                    {stepCount > 0 && (
                      <span className="section-steps">{stepCount} Steps</span>
                    )}
                  </div>
                  <div className="guide-section-content">
                    <div className="guide-section-content-inner" dangerouslySetInnerHTML={{ __html: section.html }} />
                  </div>
                </div>
              );
            })}
          </div>
          {showGoToTop && (
            <button onClick={scrollToTop} className="go-to-top-btn" title="Go to top">
              <FiArrowUp />
            </button>
          )}
        </article>
      </div>
    </>
  );
}
